import {createContext, useCallback, useEffect, useRef} from 'react'
import {useFieldArray} from 'react-hook-form'
import {Reorder} from 'framer-motion'

import {noop} from '@sdox/utils'
import {Button} from '@sdox/ui'
import {IconPlus} from '@sdox/icons'

import {MultiItemInputItem} from './MultiItemInputItem'
import {useFieldError} from '../../../hooks'

import type {ReactNode} from 'react'

interface MultiItemInputProps {
  fieldName: string
  getDefaultItemData: () => any
  label: string
  maxItemCount?: number
  renderItem: (index: number) => ReactNode
}

type MultiItemInputContextValue = {
  itemCount: number
  label: string
  deleteItem: (index: number) => void
  moveUp: (index: number) => void
  moveDown: (index: number) => void
}

export const MultiItemInputContext = createContext<MultiItemInputContextValue>({
  itemCount: 0,
  label: '',
  deleteItem: noop,
  moveUp: noop,
  moveDown: noop,
})

export const MultiItemInput = (props: MultiItemInputProps) => {
  const {fieldName, label, maxItemCount = Number.POSITIVE_INFINITY, renderItem, getDefaultItemData} = props
  const lastAddedIndex = useRef<number>(-1)
  const {fields, append, remove, swap} = useFieldArray({
    name: fieldName,
  })

  const {error} = useFieldError(fieldName)
  const dataErrors = Array.isArray(error) ? error : []
  const canAddItem: boolean = fields.length < maxItemCount

  const appendItem = useCallback((): void => {
    append(getDefaultItemData())
    lastAddedIndex.current = fields.length
  }, [append, fields.length, getDefaultItemData])

  useEffect(() => {
    if (fields.length === 0) {
      appendItem()
    }
  }, [fields.length, appendItem])

  const onMoveUp = useCallback((index: number) => {
    if (index === 0) return
    swap(index, index - 1)
  }, [swap])

  const onMoveDown = useCallback((index: number) => {
    if (index === fields.length - 1) return
    swap(index, index + 1)
  }, [fields.length, swap])

  const onDelete = useCallback((index: number) => {
    remove(index)
  }, [remove])

  return (
    <MultiItemInputContext.Provider
      value={{
        itemCount: fields.length,
        label: label,
        moveDown: onMoveDown,
        moveUp: onMoveUp,
        deleteItem: onDelete,
      }}
    >
      <div>
        <Reorder.Group
          onReorder={noop}
          values={fields}
          as={'div'}
          className={'grid gap-spacing-12'}
        >
          {fields.map((field, index) => {
            const hasError = !!dataErrors[index]
            return (
              <Reorder.Item
                as={'div'}
                value={field}
                key={field.id}
                dragListener={false}
              >
                <MultiItemInputItem
                  index={index}
                  isDefaultOpen={lastAddedIndex.current === index}
                  hasError={hasError}
                >
                  {renderItem(index)}
                </MultiItemInputItem>
              </Reorder.Item>
            )
          })}
        </Reorder.Group>
        <div className={'mt-spacing-12 pl-spacing-24'}>
          <Button
            size={'small'}
            onClick={appendItem}
            type={'button'}
            variant={'outline'}
            className={'w-full'}
            leading={<IconPlus/>}
            disabled={!canAddItem}
          >
            {label} hinzufügen
          </Button>
          {!canAddItem && <span className={'ml-spacing-4'}>Maximale Anzahl ({maxItemCount}) erreicht.</span>}
        </div>
      </div>
    </MultiItemInputContext.Provider>
  )
}
